import React, {
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    MdKeyboardArrowRight,
    MdStar,
    MdOutlineCalendarMonth,
    MdOutlinePersonOutline,
    MdOutlineBedtime,
} from 'react-icons/md';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { dispatchTrackingEvent } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import WishlistWidget from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/WishlistWidget';
import HorizontalScrollArrows from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/HorizontalScrollArrows';

const ITEM_WIDTH_PX = 300;
const ITEMS_GAP_PX = 16;
const ITEM_TOTAL_WIDTH_PX = ITEM_WIDTH_PX + ITEMS_GAP_PX;
const SCROLL_ANIMATION_DURATION_MS = 500;

// Promjene u HTML strukturi potrebno primijeniti i na "attractiveOfferAccommodation.html.twig" jer koriste isti css.
export default function FeaturedAccommodation({
    accommodation,
    initialUserWishlist,
    openLoginModal,
    eventNames: { seeMoreClick, itemClick, itemView },
}) {
    const [itemsScrollableElement, setItemsScrollableElement] = useState(null);
    const featuredAccommodationRef = useRef([]);

    useEffect(() => {
        if (IS_FRAME_SERVICE) return undefined;
        const element = featuredAccommodationRef.current;
        if (!element) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    dispatchTrackingEvent(itemView);
                    observer.unobserve(entry.target);
                }
            },
            {
                // Trigger-a se tek kad uđe sa 25% elementa.
                threshold: 0.25,
            },
        );
        observer.observe(element);

        return () => { observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLastMinuteItemSeeAll = () => {
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(seeMoreClick);
    };

    const handleLastMinuteItemTracking = () => {
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(itemClick);
    };

    return (
        <div ref={featuredAccommodationRef} className="featuredAccommodation">
            <a
                href={accommodation.hyperlink.redirectURL}
                title={accommodation.hyperlink.seoTitle}
                className="featuredAccommodation--header"
                onClick={handleLastMinuteItemSeeAll}
                onAuxClick={(event) => {
                    if (event.button === 1) {
                        handleLastMinuteItemSeeAll();
                    }
                }}
            >
                <h2 className="featured-accommodation__header-title">
                    <span className="featured-accommodation__header-title-text">&#128293; {accommodation.title}</span>
                    <span className="featured-accommodation__header-title-description" dangerouslySetInnerHTML={{ __html: accommodation.subtitleHTML }} />
                </h2>
                <span className="featured-accommodation__header-line" />
                <span className="featuredAccommodation__header-see-more">
                    <span className="featuredAccommodation__header-see-more-text">
                        {accommodation.hyperlink.redirectURLTitle}
                    </span>
                    <MdKeyboardArrowRight className="featuredAccommodation__header-see-more-icon" />
                </span>
            </a>
            <div className="featuredAccommodation__items-container">
                <div
                    ref={(el) => {
                        if (el && el !== itemsScrollableElement) {
                            setItemsScrollableElement(el);
                        }
                    }}
                    className="featuredAccommodation__items"
                >
                    {accommodation.offers.map((item) => (
                        <div key={item.unitId} className="featuredAccommodation__item-wrapper">
                            <WishlistWidget
                                unitsData={[
                                    {
                                        unitId: item.unitId,
                                        title: item.locationAndUnitNameString.split('/')[1].trim(),
                                    },
                                ]}
                                initialWishlistedUnits={
                                    initialUserWishlist
                                        ? (
                                            initialUserWishlist.find(
                                                (wishlistItem) => (
                                                    wishlistItem.units.includes(
                                                        item.unitId,
                                                    )
                                                ),
                                            )?.units ?? []
                                        )
                                        : null
                                }
                                openLoginModal={openLoginModal}
                            />
                            <a
                                className="featuredAccommodation__item"
                                data-id={item.unitId}
                                href={item.unitPresentationURL}
                                title={item.unitTitleString}
                                onClick={handleLastMinuteItemTracking}
                                onAuxClick={(event) => {
                                    if (event.button === 1) {
                                        handleLastMinuteItemTracking();
                                    }
                                }}
                            >
                                <span className="featured-accommodation__item-image-container">
                                    <img
                                        className="featuredAccommodation__item-image"
                                        loading="lazy"
                                        src={item.image.sourceURL}
                                        srcSet={[
                                            item.image.sourceURL
                                                && `${item.image.sourceURL} 1x`,
                                            item.imageSourceURLMediumSizeAlternative
                                                && `${item.imageSourceURLMediumSizeAlternative} 2x`,
                                            item.imageSourceURLLargeSizeAlternative
                                                && `${item.imageSourceURLLargeSizeAlternative} 3x`,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                        alt={item.image.altText}
                                        title={item.image.titleText}
                                    />
                                    <span className="featured-accommodation__item-overnight-stays">
                                        <MdOutlineBedtime className="featured-accommodation__item-overnight-stays-icon" />
                                        <span className="featured-accommodation__item-overnight-stays-text">
                                            {item.overnightStaysString}
                                        </span>
                                    </span>
                                </span>
                                {item.rating && (
                                    <span className="featured-accommodation__item-rating">
                                        <MdStar className="featured-accommodation__item-rating-icon" />
                                        {item.rating}
                                    </span>
                                )}
                                <h3 className="featuredAccommodation__item-title">
                                    {item.unitTitleString}
                                </h3>
                                <p className="featuredAccommodation__item-subtitle">
                                    {item.locationAndUnitNameString}
                                </p>
                                <span className="featured-accommodation__item-content">
                                    <span className="featured-accommodation__item-dates-and-capacity">
                                        <span className="featured-accommodation__item-dates">
                                            <MdOutlineCalendarMonth className="featured-accommodation__item-content-icon" />
                                            <span className="featured-accommodation__item-dates-text">
                                                <span>{item.intervalFrom} - </span><span className="text-nowrap">{item.intervalTo}</span>
                                            </span>
                                        </span>
                                        <span className="featured-accommodation__item-capacity">
                                            <MdOutlinePersonOutline className="featured-accommodation__item-content-icon" />
                                            <span className="featured-accommodation__item-capacity-text">
                                                {item.capacity}
                                            </span>
                                        </span>
                                    </span>
                                    <span className="featured-accommodation__item-price-box">
                                        <span className="featured-accommodation__item-price-box-details">
                                            <span className="featured-accommodation__item-old-price">
                                                <span className="featured-accommodation__item-old-price-text">
                                                    {item.oldPriceString}
                                                </span>
                                                <span className="featured-accommodation__item-old-price-value">
                                                    {item.priceWithoutDiscountString}
                                                </span>
                                            </span>
                                            <span className="featured-accommodation__item-new-price">
                                                <span className="featured-accommodation__item-new-price-text">
                                                    {item.newPriceString}
                                                    <span className="featured-accommodation__item-new-price-percentage">
                                                        {item.discountPercentString}
                                                    </span>
                                                </span>
                                                <span className="featured-accommodation__item-new-price-value">
                                                    {item.priceWithDiscountString}
                                                </span>
                                            </span>
                                        </span>
                                        <span className="featured-accommodation__item-price-box-separator" />
                                        <span className="featured-accommodation__item-price-box-savings">
                                            {item.discountAmountString}
                                        </span>
                                    </span>
                                </span>
                                <span className="featured-accommodation__item-see-offer">
                                    <span>{item.seeThisOfferString}</span>
                                    <MdKeyboardArrowRight className="featured-accommodation__item-see-offer-icon" />
                                </span>
                            </a>
                        </div>
                    ))}
                </div>
            </div>
            <a
                href={accommodation.hyperlink.redirectURL}
                className="featured-accommodation__see-more-bottom"
                onClick={handleLastMinuteItemSeeAll}
                onAuxClick={(event) => {
                    if (event.button === 1) {
                        handleLastMinuteItemSeeAll();
                    }
                }}
            >
                <span className="featured-accommodation__see-more-bottom-text">
                    {accommodation.hyperlink.redirectURLTitle}
                </span>
                <MdKeyboardArrowRight className="featured-accommodation__see-more-bottom-icon" />
            </a>
            <div className="d-block d-lg-none">
                <HorizontalScrollArrows
                    scrollableElement={itemsScrollableElement}
                    scrollAmountPx={ITEM_TOTAL_WIDTH_PX}
                    durationMs={SCROLL_ANIMATION_DURATION_MS}
                    alignRight={true}
                />
            </div>
        </div>
    );
}
